import React, { useState } from 'react';
import { TranslationFunction } from 'i18next';
import { inject, observer } from 'mobx-react';
import { Store } from './../../../stores';
import { translate } from 'react-i18next';
import { RouteComponentProps, withRouter } from 'react-router';
import classnames from 'classnames';
import { Button } from 'reactstrap';
import { convertTranslation } from './../../../utils/convertTranslations';
import gql from 'graphql-tag';
import { withApollo, WithApolloClient, Mutation } from 'react-apollo';
import { safeMobxInject } from './../../../stores/storeInjectionHelpers';
import { Profile } from './../../../stores/profile';

import './newCustomerPopup.scss';
import LoadingButton from '../../../ui/Button/LoadingButton/LoadingButton';

const VALIDATE_PRO_USER_IS_TRUSTED = gql`
  mutation validateProUser(
    $proUserEmail: String!
  ) {
    validateProUser(
      proUserEmail: $proUserEmail
    )
  }
`;

interface IMutationParameters {
  variables: { 
    proUserEmail: string,
  };
}

interface IProps extends RouteComponentProps {
  t: TranslationFunction;
  Store?: Store;
  onClose: () => void;
}

interface IStoresProps {
  profile: Profile
}

interface IMutationResponse {
  validateProUser: boolean;
}

interface IEnergyProvider {
  isMylight150: boolean;
  isOtherProvider: boolean;
}

interface IRegistrationType {
  isEnedis: boolean;
  isStrasbourg: boolean;
}

const initEnergyProviderOptions: IEnergyProvider = {
  isMylight150: false,
  isOtherProvider: false,
}

const initRegistrationOptions: IRegistrationType = {
  isEnedis: false,
  isStrasbourg: false,
};

const NewCustomerPopup = safeMobxInject<IStoresProps, IProps>(
  stores => ({
    profile: stores.Store.profile
  }),
  ({
    t,
    onClose,
    profile, 
    history
  }: WithApolloClient<IProps & IStoresProps>) => {
  const [showForm, setShowForm] = useState<boolean>(false);
  const [energyProviderOptions, setEnergyProviderOptions] = useState<IEnergyProvider>(initEnergyProviderOptions);
  const [registrationOptions, setRegistrationOptions] = useState<IRegistrationType>(initRegistrationOptions);
  const [showUPMContent, setShowUPMContent] = useState<boolean>(false);
  const [isProUserWhitelisted, setProUserWhitelisted] = useState<boolean>(false);
  const [showPreForm, setShowPreForm] = useState<boolean>(true);

  const onValidateProUser = (
    validateProUser: (mutationParameters: IMutationParameters) => void
  ) => {
    validateProUser({
        variables: {
          proUserEmail: profile.email
        }
      })
  };

  const initPreFormFields = [
    {
      name: 'isMylight150',
      value: energyProviderOptions.isMylight150,
      text: 'NewCustomerPopup:isMylight150EnergyContract',
    },
    {
      name: 'isOtherProvider',
      value: energyProviderOptions.isOtherProvider,
      text: 'NewCustomerPopup:isNotMylight150EnergyContract',
    },
  ];

  const initRegistrationFields = [
    {
      name: 'isEnedis',
      value: registrationOptions.isEnedis,
      text: 'NewCustomerPopup:isEnedisProvider',
    },
    {
      name: 'isStrasbourg',
      value: registrationOptions.isStrasbourg,
      text: 'NewCustomerPopup:isStrasbourgProvider',
    }
  ];

  const onUpdateEnergyProviderOptions = (key: string, value: boolean): void => {
    setEnergyProviderOptions({
      ...initEnergyProviderOptions,
      [key]: value
    });
  };

  const onUpdateRegistrationOptions = (key: string, value: boolean): void => {
    setRegistrationOptions({
      ...initRegistrationOptions,
      [key]: value
    });
  };

  const onSubmitPreForm = () => {
    const {
      isMylight150,
      isOtherProvider,
    } = energyProviderOptions;

    if (isOtherProvider && !isMylight150) {
      history.push(`/createCustomer`);
    } else if (isMylight150 && !isOtherProvider) {
      setShowForm(true);
      setShowPreForm(false);
    }
  }

  const onSubmit = () => {
    const {
      isEnedis,
      isStrasbourg
    } = registrationOptions;
    
    if (isStrasbourg) {
      history.push(`/createCustomer`);
    } else if (isEnedis) {
      setShowForm(false);
      onRedirectToMylight150Pro();
    }
  };

  const onRedirectToMylight150Pro = () => {
    window.open(t('ConstellationMasterPopup:constellationAppUrl'), '_blank');
    onClose();
  };

  return (
    <div className={'popup-container'}>
      <div className={'popup-inner-container'}>
          <i
            className="icon-cross"
            color="primary"
            onClick={ onClose }
          />

        { showPreForm && <div>
          <p className={'popup-header'}>
              { t('NewCustomerPopup:newCustomerPopupPreFormTitle') }
            </p>

            <ul className="popup-list">
              { initPreFormFields.map(item => (
                <li
                  key={ item.name }
                  className={classnames(
                    'popup-list__item',
                    item.value ? 'selected' : '',
                  )}
                  onClick={
                  () => onUpdateEnergyProviderOptions(item.name, !item.value) 
                }>{ t(item.text) }</li>
              )) }
            </ul>
        </div> }
        
        { showForm && <div>
            <p className={'popup-header'}>
              { t('NewCustomerPopup:newCustomerPopupFormTitle') }
            </p>

            <ul className="popup-list">
              { initRegistrationFields.map(item => (
                <li
                  key={ item.name }
                  className={classnames(
                    'popup-list__item',
                    item.value ? 'selected' : '',
                  )}
                  onClick={
                  () => onUpdateRegistrationOptions(item.name, !item.value) 
                }>
                  { 
                    item.name === 'isStrasbourg' &&
                    <span className="popup-list__item-notification">
                      { t('NewCustomerPopup:strasbourgNotification') }
                    </span> 
                  }
                  { t(item.text) }
                </li>
              )) }
            </ul>

          </div> }
        { showUPMContent && <div>
            <p className={'popup-header'}>
              { t('NewCustomerPopup:newCustomerPopupMyBatteryTitle') }
            </p>

            <p>
              { convertTranslation(t('NewCustomerPopup:newCustomerPopupMyBatteryContent')) }
            </p>
          </div> }

        <div
          className={classnames(
            'popup-buttons-container',
            'horizontal'
          )}
        >
          { showPreForm && 
            <Mutation
                mutation={ VALIDATE_PRO_USER_IS_TRUSTED }
                onCompleted={(data: IMutationResponse) => {
                  setProUserWhitelisted(!!data.validateProUser);
                  onSubmitPreForm();
                }}
              >
                {(validateProUser, { loading }) => {
                  return (
                    <LoadingButton
                      id={ `pre-form-button` }
                      className={'popup-button'}
                      loading={ loading }
                      colorClass="primary"
                      onClick={ () => {
                        !isProUserWhitelisted && onValidateProUser(validateProUser);
                      } }
                    >
                      { t('NewCustomerPopup:newCustomerPopupBtn') }
                    </LoadingButton>
                  )
                }}
            </Mutation> }
          { showForm && <Button
            className={'popup-button'}
            color="primary"
            onClick={ onSubmit }
          >
            { t('NewCustomerPopup:newCustomerPopupBtn') }
          </Button> }

          { showUPMContent && <Button
            className={'popup-button'}
            color="primary"
            onClick={ onClose }
          >
            { t('NewCustomerPopup:newCustomerPopupMyBatteryBtn') }
          </Button> }
        </div>
      </div>
    </div>
  );
});

export default translate(['Customer', 'Tenant', 'NewCustomerPopup'])(
  withRouter(withApollo(NewCustomerPopup))
);